import { Control, Controller, UseFormResetField } from 'react-hook-form'
import { Dropdown } from 'primereact/dropdown'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// local imports
import doctorGray from 'assets/icons/doctor-gray.svg'
import branchList from 'assets/icons/list-branch.svg'
import { IHealthLocationList, IDoctorSpecialtyList, IFilteredDoctorList, IDoctorSearchForm } from 'types/HealthType'
import DoctorPhoto from 'components/home/on-campus-services/health-service/doctor/DoctorPhoto'

interface IDoctorFormProps {
  control: Control<IDoctorSearchForm>
  resetField: UseFormResetField<IDoctorSearchForm>
  doctorList: IFilteredDoctorList[] | []
  specialtyList: IDoctorSpecialtyList[] | undefined
  locationList: IHealthLocationList[] | undefined
}

export default function DoctorSearchForm({
  control,
  resetField,
  doctorList,
  specialtyList,
  locationList,
}: IDoctorFormProps) {
  const { t } = useTranslation()

  return (
    <div className="d-flex flex-column gap-2">
      {/* Location */}
      <Controller
        name="locationId"
        control={control}
        render={({ field }) => (
          <DropdownWrapper>
            <label className="autocomplete-title">{t('most-used-words.location')}</label>
            <i className="autocomplete-icon p-input-icon-left pi pi-map-marker"></i>
            <StyledCustomDropdown
              id="doctorLocation"
              emptyMessage={t('most-used-words.option-not-found')}
              emptyFilterMessage={t('most-used-words.option-not-found')}
              name="locationId"
              placeholder={t('input-texts.choose-location')}
              optionLabel="name"
              optionValue="id"
              valueTemplate={(opt, props) => valueTemplate(opt, props)}
              options={locationList}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.value)
                resetField('specialtyId')
                resetField('doctor')
              }}
            />
          </DropdownWrapper>
        )}
      />

      {/* Specialty */}
      <Controller
        name="specialtyId"
        control={control}
        render={({ field }) => (
          <DropdownWrapper>
            <img className="autocomplete-icon p-input-icon-left" src={branchList} alt="logo" width={18} height={18} />
            <StyledCustomDropdown
              id="doctorSpecialty"
              emptyMessage={t('most-used-words.option-not-found')}
              emptyFilterMessage={t('most-used-words.option-not-found')}
              name="specialtyId"
              placeholder={t('input-texts.choose-specialty')}
              optionLabel="name"
              optionValue="id"
              options={specialtyList}
              value={field.value}
              onChange={(e) => {
                field.onChange(e.value)
                resetField('doctor')
              }}
            />
          </DropdownWrapper>
        )}
      />

      {/* Doctor */}
      <Controller
        name="doctor"
        control={control}
        render={({ field }) => (
          <DropdownWrapper>
            <img className="autocomplete-icon p-input-icon-left" src={doctorGray} alt="logo" width={20} height={20} />
            <StyledCustomDropdown
              id="doctorDropdown"
              className="doctor-dropdown"
              emptyMessage={t('most-used-words.option-not-found')}
              appendTo="self"
              placeholder={t('input-texts.choose-doctor')}
              optionLabel="name"
              options={doctorList}
              valueTemplate={doctorValueTemplate}
              itemTemplate={doctorOptTemplate}
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
            />
          </DropdownWrapper>
        )}
      />
    </div>
  )
}

function valueTemplate(options: IHealthLocationList, props: any) {
  return <div className="selectedVal">{options?.name || props?.placeholder}</div>
}

function doctorValueTemplate(options: IFilteredDoctorList, props: any) {
  if (!options) return <>{props?.placeholder}</>
  const label = `${options?.title} ${options?.name} ${options?.surname}`
  return <>{label || props?.placeholder}</>
}

function doctorOptTemplate(options: IFilteredDoctorList) {
  return (
    <StyledDropdownItems id="doctorDropdownItem">
      <div className="doc-photo">
        <DoctorPhoto
          doctorId={options?.id || 0}
          employeeId={options?.employeeId || 0}
          IsAbsolute={false}
          size={[40, 40]}
        />
      </div>
      <div className="doc-wrapper">
        <div className="doc-info">
          <div className="doc-name-specialty">
            <span className="doc-name">
              {options.title} {options.name} {options.surname}
            </span>
            <span className="doc-specialty">({options.specialty})</span>
          </div>

          <span className="doc-location"> {options.location.split(',').slice(0, 2).join(',')} </span>
        </div>
        <div className="doc-explanation">
          {options.explanation ||
            'Amet erat in tempor leo sed sagittis cras sit aliquet cursus consectetur pretium mi, lacus, commodo auctor vulputate '}
        </div>
      </div>
    </StyledDropdownItems>
  )
}

const DropdownWrapper = styled.div`
  position: relative;
  .autocomplete-title {
    position: absolute;
    left: 35px;
  }

  .autocomplete-icon {
    position: absolute;
    left: 8px;
    top: 16px;
    z-index: 1;
    color: #a8a8a8;
  }

  .selectedVal,
  .p-dropdown .p-dropdown-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .selectedVal {
    margin-top: 0.5rem;
    font-weight: 700;
  }

  .p-dropdown .p-dropdown-label {
    padding-left: 2rem;
    padding-right: 0;
  }
`

const StyledCustomDropdown = styled(Dropdown)`
  width: 100%;
  height: 50px;
  border-radius: 10px;
  align-items: center;

  .p-dropdown-panel {
    z-index: 1004 !important;
    position: absolute !important;
    left: 0px !important;
    top: 52px !important;
    min-width: 100% !important;
    min-height: 100% !important;
    background: #ffffff !important;
    transform-origin: center top !important;
  }

  .p-dropdown-items-wrapper {
    max-height: 220px !important;
    max-width: 530px !important;

    &::-webkit-scrollbar-track {
      -webkit-box-shadow: none !important;
      background-color: #d3d3d3 !important;
      border-radius: 10px;
    }

    &::-webkit-scrollbar {
      height: 6px;
      width: 6px !important;
      background-color: #d3d3d3;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #7c7c7c;
      border-radius: 10px;
    }
  }

  &.doctor-dropdown {
    .p-dropdown-panel {
      top: 55px !important;
      background: none !important;
      box-shadow: none !important;
      border: 1px solid #cecece;
    }

    .p-dropdown-items {
      padding: 0 !important;
    }

    .p-dropdown-item {
      padding: 0 10px;
      border-top: 1px solid #cecece;
      border-bottom: 1px solid #cecece;

      &:first-of-type {
        border-top: none !important;
      }
      &:last-of-type {
        border-bottom: none !important;
      }
    }

    .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover {
      border-radius: 15px !important;
    }
    .p-dropdown-item.p-highlight {
      border-radius: 15px !important;
    }
  }
`
const StyledDropdownItems = styled.div`
  padding: 0.5rem 0.2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100% !important;

  .doc-wrapper {
    width: 100% !important;
    margin-left: 8px;

    .doc-explanation {
      width: 440px !important;
      font-size: 12px;
      text-overflow: ellipsis !important;
      overflow: hidden;
    }

    .doc-info {
      color: #363636;
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      // column-gap: 0.1rem;

      .doc-name-specialty {
        display: flex;
        align-items: center;
        column-gap: 4px;
      }

      .doc-name {
        font-size: 14px;
        font-weight: bold;
      }

      .doc-specialty {
        font-size: 13px;
      }

      .doc-location {
        font-size: 11px;
      }
    }
  }
`
